import { type SVGProps, useState } from "react";

import { clsx } from "@alpacahq/ui/old";

import * as Icons from "~/assets";
import { useAssetLogoManifest } from "~/v2/hooks/useAssetLogoManifest";
import {
  type AssetLogoTheme,
  useAssetLogoTheme,
} from "~/v2/hooks/useAssetLogoTheme";

export type IconSize = "sm" | "base" | "lg" | "xl";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: string;
  /** sm (16px), base (20px), lg (26px), xl (32px) */
  size?: IconSize;
  className?: string;
}

const SIZE_CLASSES: Record<IconSize, string> = {
  sm: "w-4 h-4",
  base: "w-5 h-5",
  lg: "w-[26px] h-[26px]",
  xl: "w-8 h-8",
};

const TEXT_SIZE_CLASSES: Record<IconSize, string> = {
  sm: "text-[7px]",
  base: "text-[9px]",
  lg: "text-[10px]",
  xl: "text-[13px]",
};

const failedLogos = new Set<string>();

function failedLogosKey(symbol: string, theme: AssetLogoTheme): string {
  return `${symbol}:${theme}`;
}

const InitialsPlaceholder = ({
  name,
  size = "base",
  className,
  theme = "light",
}: {
  name: string;
  size?: IconSize;
  className?: string;
  theme?: AssetLogoTheme;
}) => {
  const combinedClassName = clsx(SIZE_CLASSES[size], className);
  const initials = name.slice(0, 2).toUpperCase();
  const PlaceholderSymbol =
    theme === "dark"
      ? Icons.PlaceholderDarkSymbol
      : Icons.PlaceholderLightSymbol;

  return (
    <div className={clsx("relative inline-flex", combinedClassName)}>
      <PlaceholderSymbol className={combinedClassName} />
      <div
        className={clsx(
          "absolute inset-0 flex items-center justify-center",
          "pointer-events-none select-none",
          "font-semibold text-[#565656]",
          TEXT_SIZE_CLASSES[size],
        )}
      >
        {initials}
      </div>
    </div>
  );
};

const RemoteAssetIcon = ({
  symbol,
  baseUrl,
  size = "base",
  className,
  theme,
  hasDarkVariant,
}: {
  symbol: string;
  baseUrl: string;
  size?: IconSize;
  className?: string;
  theme: AssetLogoTheme;
  hasDarkVariant: boolean;
}) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);
  const normalizedSymbol = symbol.replace("/", "-");

  if (failed) {
    return (
      <InitialsPlaceholder
        name={symbol}
        size={size}
        className={className}
        theme={theme}
      />
    );
  }

  const lightSrc = `${baseUrl}/${normalizedSymbol}.svg`;
  const darkSrc = `${baseUrl}/dark/${normalizedSymbol}.svg`;

  const tryDarkFirst = theme === "dark" && !triedFallback && hasDarkVariant;
  const src = tryDarkFirst ? darkSrc : lightSrc;

  const sizeClass = SIZE_CLASSES[size];
  const combinedClassName = clsx(sizeClass, className);

  const handleError = () => {
    if (tryDarkFirst) {
      setTriedFallback(true);
    } else {
      failedLogos.add(failedLogosKey(symbol, "light"));
      setFailed(true);
    }
  };

  return (
    <span className={clsx("inline-block shrink-0", combinedClassName)}>
      {!loaded ? (
        <span
          className={clsx("block animate-pulse rounded bg-gray-200", sizeClass)}
        />
      ) : (
        <img
          src={src}
          alt={symbol}
          className={clsx("block", sizeClass)}
          onError={handleError}
        />
      )}
      {!loaded && (
        <img
          src={src}
          alt=""
          aria-hidden
          className="sr-only"
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
    </span>
  );
};

/** Manifest-gated asset icon; falls back to initials when absent or on error. */
export const Icon = ({ name, size = "base", className }: IconProps) => {
  const theme = useAssetLogoTheme();
  const { availableSymbols, symbolsWithDark, isLoading } =
    useAssetLogoManifest();
  const baseUrl = window.env?.ASSET_LOGO_BASE_URL;
  const lightFailedKey = failedLogosKey(name, "light");

  if (baseUrl && isLoading) {
    const sizeClass = SIZE_CLASSES[size];
    return (
      <span className={clsx("inline-block shrink-0", sizeClass, className)}>
        <span
          className={clsx("block animate-pulse rounded bg-gray-200", sizeClass)}
        />
      </span>
    );
  }

  const symbolInManifest = availableSymbols?.has(name) ?? false;
  const hasDarkVariant = symbolsWithDark?.has(name) ?? false;

  if (baseUrl && symbolInManifest && !failedLogos.has(lightFailedKey)) {
    return (
      <RemoteAssetIcon
        symbol={name}
        baseUrl={baseUrl}
        size={size}
        className={className}
        theme={theme}
        hasDarkVariant={hasDarkVariant}
      />
    );
  }

  return (
    <InitialsPlaceholder
      name={name}
      size={size}
      className={className}
      theme={theme}
    />
  );
};
